import {
    CellKeyDownEvent,
    ColDef,
    Column,
    GetRowIdFunc,
    GetRowIdParams,
    GridApi,
    GridOptions,
    ICellRendererParams,
    RowClassParams,
    SizeColumnsToFitGridStrategy,
} from 'ag-grid-community';
import { CheckBoxCellRendererComponent } from '../../../shared/components/grid/cell-renderers/check-box-cell-renderer/check-box-cell-renderer.component';
import { FormCellRendererBaseComponent } from '../../../shared/components/grid/cell-renderers/form-cell-renderer-base/form-cell-renderer-base.component';
import { FormInputCellRendererComponent } from '../../../shared/components/grid/cell-renderers/form-input-cell-renderer/form-input-cell-renderer.component';
import { FormSelectCellRendererComponent } from '../../../shared/components/grid/cell-renderers/form-select-cell-renderer/form-select-cell-renderer.component';
import { GridToolbarOptions } from '../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import {
    formatToDate,
    parseDateToDateOnly,
    parseDateToEasternGridFormat,
} from '../../functions/date-helpers';
import { nameOf } from '../../functions/string-helpers';
import { ActionableGridEntity } from './actionable-grid-entity';
import { GridTooltipHelperMessages } from './constants/grid-tooltip-messages';
import { DeletableGridEntity } from './deletable-grid-entity';
import { SelectableGridEntity } from './selectable-grid-entity';

/**
 * Helper class for grid-related operations.
 */
export class GridHelper {
    private static defaultColDef: ColDef = {
        filter: true,
        filterParams: {
            filterPlaceholder: 'Search',
            filterOptions: ['contains'],
            maxNumConditions: 1,
            buttons: ['clear'],
        },
        cellDataType: 'text', // Allow numeric data types to be filtered as text (contains, etc.)
        minWidth: 130,
        resizable: false,
        cellStyle: {
            textAlign: 'center',
        },
        cellClass: 'flex items-center justify-center leading-snug',
        wrapText: true,
        autoHeight: true,
        suppressMovable: true,
    };

    /**
     * Column definition to disable the focus border for cells that contain form controls.
     * If the cell contains a form cell renderer and the form control is enabled, the focus border is disabled.
     * Otherwise, the focus border is enabled.
     */
    public static disableCellFocusBorderColDef: ColDef = {
        cellClassRules: {
            'disable-ag-cell-focus': () => true,
        },
    };

    public static actionCodeColDef: ColDef = {
        headerName: 'A/M/D',
        sortable: false,
        field: nameOf<ActionableGridEntity>('actionCode'),
        filter: false,
        maxWidth: 85,
        headerTooltip: GridTooltipHelperMessages.actionCodeDescriptionTooltip,
    };

    public static deleteRowColDef: ColDef = {
        headerName: 'Delete',
        field: nameOf<DeletableGridEntity>('isDeleted'),
        filter: false,
        sortable: false,
        maxWidth: 80,
        cellRenderer: CheckBoxCellRendererComponent,
        ...GridHelper.disableCellFocusBorderColDef,
    };

    public static selectRowColDef: ColDef = {
        headerName: 'Select',
        field: nameOf<SelectableGridEntity>('isSelected'),
        filter: false,
        sortable: false,
        maxWidth: 80,
        cellRenderer: CheckBoxCellRendererComponent,
        ...GridHelper.disableCellFocusBorderColDef,
    };

    public static dragRowColDef(enableDrag: boolean = true): ColDef {
        return {
            filter: false,
            sortable: false,
            maxWidth: 40,
            suppressSizeToFit: true,
            rowDrag: enableDrag,
        };
    }

    public static defaultFormInputCellRendererColDef: ColDef = {
        cellRenderer: FormInputCellRendererComponent,
        ...GridHelper.disableCellFocusBorderColDef,
    };

    public static defaultDeleteRowCellRendererColDef: ColDef = {
        cellRenderer: CheckBoxCellRendererComponent,
        ...GridHelper.disableCellFocusBorderColDef,
    };

    public static defaultFormSelectCellRendererColDef: ColDef = {
        cellRenderer: FormSelectCellRendererComponent,
        ...GridHelper.disableCellFocusBorderColDef,
    };

    public static defaultDateTimeFilterParams = {
        maxNumConditions: 1,
        numAlwaysVisibleConditions: 1,
        buttons: ['clear'],
        filterOptions: [
            {
                displayKey: 'equals',
                displayName: 'Equals',
                predicate: ([filterDate]: Date[], cellValue: Date) => {
                    if (!cellValue || !filterDate) {
                        return false;
                    }

                    const startOfDay = new Date(filterDate);
                    startOfDay.setHours(0, 0, 0, 0);

                    const endOfDay = new Date(filterDate);
                    endOfDay.setHours(23, 59, 59, 999);

                    return cellValue >= startOfDay && cellValue <= endOfDay;
                },
            },
            'greaterThan',
            'lessThan',
            'inRange',
        ],
    };

    public static defaultFormDateRendererColDef: ColDef = {
        ...GridHelper.disableCellFocusBorderColDef,
        valueFormatter: (params) => {
            if (params.value) {
                return params.value.toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                });
            }
            return '';
        },
        valueGetter: (params) => {
            let dateValue = params.data[params.colDef.field as string];
            if (dateValue !== null && dateValue !== undefined) {
                dateValue = String(dateValue);
            }
            // Format the date string here to prevent creating an ISO string, which causes timezone issues when exporting to CSV
            return dateValue ? new Date(parseDateToDateOnly(dateValue)) : null;
        },
    };

    /**
     * Column definition for date columns that should be sorted as dates.
     * Uses valueFormatter to display dates in Eastern time format while keeping Date objects for sorting.
     */
    public static defaultDateColDef: ColDef = {
        cellDataType: 'date',
        valueFormatter: (params) => {
            if (!params.value) {
                return '';
            }

            return parseDateToEasternGridFormat(params.value);
        },
    };

    /**
     * Column definition for date time columns that should be sorted as date times.
     * Uses valueGetter to convert date time strings to Date objects for correct sorting and filtering,
     * and uses the inherited valueFormatter from defaultDateColDef to display values in Eastern grid format.
     */
    public static defaultDateTimeRendererColDef: ColDef = {
        ...GridHelper.defaultDateColDef,
        valueGetter: (params) => {
            const val = params.data[params.colDef.field as string];
            if (!val) {
                return null;
            }

            if (val instanceof Date) {
                return val;
            }

            return formatToDate(val);
        },
        filterParams: {
            ...GridHelper.defaultDateTimeFilterParams,
        },
    };

    public static defaultToolbarOptions: GridToolbarOptions = {
        refreshDataForExport: () => {},
        quickFilter: true,
        exportCsv: true,
        clearFilters: true,
    };

    public static defaultTooltipDelaysGridOptions: GridOptions = {
        tooltipShowDelay: 0,
        tooltipHideDelay: 999999,
    };

    public static defaultNumericFilterParams = {
        maxNumConditions: 1,
        numAlwaysVisibleConditions: 1,
        buttons: ['clear'],
        filterOptions: [
            'equals',
            'notEqual',
            'greaterThanOrEqual',
            'greaterThan',
            'lessThanOrEqual',
            'lessThan',
        ],
    };

    public static defaultDateFilterParams = {
        maxNumConditions: 1,
        numAlwaysVisibleConditions: 1,
        buttons: ['clear'],
        filterOptions: [
            'equals',
            'notEqual',
            'lessThan',
            'greaterThan',
            'inRange',
            'blank',
            'notBlank',
        ],
    };

    /**
     * Default grid options for the grid.
     * Usage: in component that implements IGridComponent,
     *   `gridOptions: GridOptions = { ...GridHelper.defaultGridOptions, columnDefs: this.columnDefs }`.
     */
    public static defaultGridOptions: GridOptions = {
        ...this.defaultTooltipDelaysGridOptions,
        autoSizeStrategy: {
            type: 'fitGridWidth',
        } as SizeColumnsToFitGridStrategy,
        defaultColDef: this.defaultColDef,
        localeText: {
            pageSizeSelectorLabel: 'Rows',
            ariaPageSizeSelectorLabel: 'Rows per Page',
        },
        pagination: true,
        paginationPageSizeSelector: [25, 50, 150, 250, 500],
        paginationPageSize: 25,
        enableCellTextSelection: true,
        accentedSort: true,
    };

    public static defaultGridOptionsNoPagination: GridOptions = {
        ...this.defaultTooltipDelaysGridOptions,
        autoSizeStrategy: {
            type: 'fitGridWidth',
        } as SizeColumnsToFitGridStrategy,
        defaultColDef: this.defaultColDef,
        pagination: false,
        enableCellTextSelection: true,
        accentedSort: true,
    };

    /**
     * Default grid options for grids with form fields; strips CSS that may break form field rendering.
     */
    public static defaultFormFieldGridOptions: GridOptions = {
        ...this.defaultGridOptions,
        defaultColDef: {
            ...this.defaultGridOptions.defaultColDef,
            cellClass: '',
        },
    };

    public static defaultGridOptionsNoPaginationUnstyledCells: GridOptions = {
        ...this.defaultGridOptionsNoPagination,
        defaultColDef: {
            ...this.defaultGridOptions.defaultColDef,
            cellClass: '',
        },
    };

    public static defaultGridOptionsRowDrag: GridOptions = {
        rowDragManaged: true,
        animateRows: true,
    };

    /**
     * @param params Row Id parameters containing the data for the row.
     * @returns function that returns the row Id.
     */
    public static defaultGetRowId: GetRowIdFunc = (params: GetRowIdParams) => {
        if (!params.data.id && !params.data.tempId) {
            throw new Error('Row data must have either id or tempId defined.');
        }

        return String(params.data.id ?? params.data.tempId);
    };

    /**
     * Generates a temporary Id for new rows.
     *
     * @returns A string representing a temporary Id.
     */
    public static generateTempRowId(): string {
        return `temp-${Math.random().toString(36).substring(2, 9)}`;
    }

    /**
     * Gets the export file name for the grid.
     * @param suffix Suffix to append to the file name.
     * @param id Optional ID to include in the file name.
     * @returns Export file name.
     */
    public static getExportFileName(
        suffix: string,
        id?: string | null | undefined,
    ): string {
        return `Cevo_${id ? `${id}_` : ''}${suffix}`;
    }

    public static getSelectRowColDef(field: string): ColDef {
        return {
            headerName: 'Select',
            field: field,
            filter: false,
            sortable: false,
            maxWidth: 80,
            cellRenderer: CheckBoxCellRendererComponent,
            ...GridHelper.disableCellFocusBorderColDef,
        };
    }

    /**
     * Gets the grid options with error highlighting enabled.
     *
     * @param validationFn function to validate the data
     * @param isBeingEdited function to check if the data is being edited
     * @returns GridOptions with error highlighting
     */
    public static getErrorHighlightingGridOptions<T>(
        validationFn: (data: T) => boolean,
        isBeingEditedFn?: (data: T) => boolean,
    ): GridOptions {
        return {
            suppressRowHoverHighlight: true, // Used to prevent the hover effect from overriding the error highlighting
            rowClassRules: {
                // Use rowClassRules over getRowClass. So it'll remove the class when condition is no longer true.
                'bg-grid-row-editing hover:bg-grid-row-hover-editing': (
                    params,
                ) => {
                    return this.isRowBeingEdited(params, isBeingEditedFn);
                },
                'bg-grid-row-error hover:bg-grid-row-hover-error': (params) => {
                    return (
                        !this.isRowBeingEdited(params, isBeingEditedFn) &&
                        !this.isRowValid(params, validationFn)
                    );
                },
                'hover:bg-grid-row-hover-neutral': (params) => {
                    return (
                        !this.isRowBeingEdited(params, isBeingEditedFn) &&
                        this.isRowValid(params, validationFn)
                    );
                },
            },
        };
    }

    /**
     * Gets the grid options to allow the form cell renderer to get focused when a cell is focused.
     * @returns GridOptions to focus on the form cell renderer when a cell is focused instead of the AgGrid cell itself.
     */
    public static getFormCellRendererOnCellFocusedGridOptions(): GridOptions {
        return {
            /**
             * (This event happens before the cell if focused)
             * When the tab key is pressed, if the last cell is focused, mark that the cell is not focused anymore.
             * When the last cell in the grid is focused, the next forward tab will call the onCellFocused event for the same cell.
             * This is to prevent the cell from being focused when the user presses tab again.
             *
             * @param event
             * @returns
             */
            onCellKeyDown: (event: CellKeyDownEvent): void => {
                const keyboardEvent = event.event as KeyboardEvent;

                if (
                    keyboardEvent.key !== 'Tab' ||
                    event.column === null ||
                    event.rowIndex === null
                ) {
                    return;
                }

                const lastRowIndex = event.api.getDisplayedRowCount() - 1;
                const allColumns = event.api.getAllDisplayedColumns();
                const lastColumn = allColumns[allColumns.length - 1];

                const renderer = this.tryGetFormCellRendererFromGridApi(
                    event.api,
                    event.column,
                    event.rowIndex,
                );

                if (renderer === undefined) {
                    return;
                }

                if (
                    event.rowIndex === lastRowIndex &&
                    event.column === lastColumn
                ) {
                    renderer.updateIsFocusedState(false);
                }
            },

            /**
             * When a cell is focused, update focused state for all other form renderers, then call focus on the currently focused renderer.
             * @param event
             * @returns
             */
            onCellFocused: (event): void => {
                if (event.column === null || event.rowIndex === null) {
                    return;
                }

                const renderer = this.tryGetFormCellRendererFromGridApi(
                    event.api,
                    event.column,
                    event.rowIndex,
                );

                if (renderer === undefined) {
                    return;
                }

                renderer.updateIsFocusedState(false);

                const allRenderers =
                    event.api.getCellRendererInstances() as FormCellRendererBaseComponent<ICellRendererParams>[];

                // Tell all other renderers that they are not focused
                // Renderer may be a custom cell renderer that doesn't extend from FormCellRendererBaseComponent, so check if the method exists
                for (const otherRenderer of allRenderers) {
                    if (
                        otherRenderer.params.column?.getColId() !==
                            renderer.params.column?.getColId() &&
                        otherRenderer.updateIsFocusedState !== undefined
                    ) {
                        otherRenderer.updateIsFocusedState(false);
                    }
                }

                renderer.onFocus();
            },
        };
    }

    /**
     * @param params Row class parameters containing the data for the row.
     * @param isBeingEdited function to check if the row is being edited.
     * @returns True if the row is being edited, false otherwise.
     */
    private static isRowBeingEdited<T>(
        params: RowClassParams<T>,
        isBeingEdited?: (data: T) => boolean,
    ): boolean {
        return params.data && isBeingEdited
            ? isBeingEdited(params.data)
            : false;
    }

    /**
     * @param params Row class parameters containing the data for the row.
     * @param validationFn function to validate the row data.
     * @returns True if the row is valid, false otherwise.
     */
    private static isRowValid<T>(
        params: RowClassParams<T>,
        validationFn: (data: T) => boolean,
    ): boolean {
        return params.data ? validationFn(params.data) : false;
    }

    /**
     * Tries to get the form cell renderer from the grid API.
     * @param api
     * @param column
     * @param rowIndex
     * @returns
     */
    private static tryGetFormCellRendererFromGridApi(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        api: GridApi<any>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        column: string | Column<any>,
        rowIndex: number,
    ): FormCellRendererBaseComponent<ICellRendererParams> | undefined {
        // getCellRendererInstances only returns viewport rows, so resolve by row node
        // instead of indexing by absolute rowIndex.
        const rowNode = api.getDisplayedRowAtIndex(rowIndex);

        if (rowNode === undefined) {
            return undefined;
        }

        return api
            .getCellRendererInstances({
                columns: [column],
                rowNodes: [rowNode],
            })
            .filter(
                (r) => r instanceof FormCellRendererBaseComponent,
            )[0] as FormCellRendererBaseComponent<ICellRendererParams>;
    }
}
